export type Validation = {
  errorMessage?: string
  pattern?: string
  required?: boolean
  fun?: (field: unknown, secondField?: unknown) => { valid: false; message: string } | { valid: true }
}

export interface FormField<T> {
  fieldName: string
  displayName: string
  value: T
  draftValue?: string
  displayValue?: string
  readonly?: boolean
  placeholder?: string
  locked?: boolean
  isSensitive?: boolean
}

export interface BooleanFormField extends FormField<boolean> {
  positiveLabel: string
  negativeLabel: string
}

export interface ValidationFormField<T> extends FormField<T> {
  variant: string
  feedback: string
  validation: Validation
  tooltip?: string
}

export type StringFormField = FormField<string>

export type FormFieldTypes = FormField<unknown> | ValidationFormField<unknown>

export const validateFormField = (field: ValidationFormField<unknown>): ValidationFormField<unknown> => {
  const value = field?.value
  if (field.validation.required && (!field.value || ((value as string).trim && (value as string).trim() === ''))) {
    return {
      ...field,
      variant: 'error',
      feedback: `${field.displayName} kan ikke være tom`,
    }
  }

  if (field.validation.pattern && field.validation.errorMessage) {
    if (field.value !== '' && !RegExp(field.validation.pattern).test(value as string)) {
      return {
        ...field,
        variant: 'error',
        feedback: field.validation.errorMessage,
      }
    }
  }

  if (field.validation.fun) {
    const validate = field.validation.fun(field.value)
    if (!validate.valid) {
      return { ...field, variant: 'error', feedback: validate.message }
    }
  }

  return { ...field, variant: '', feedback: '' }
}

export interface StringMap {
  [key: string]: unknown
}

export interface FormMap {
  [key: string]: FormField<unknown>
}

export function validateAllFields<T extends StringMap>(state: T): T {
  let newState = state
  Object.keys(state).forEach((key: string) => {
    const field = state[key] as ValidationFormField<unknown>
    if (field.validation) {
      newState = { ...newState, [key]: validateFormField(field) }
    }
  })
  return newState
}

export function validateFields(fields: Array<FormFieldTypes>) {
  return fields.map((field) => {
    const validationField = field as ValidationFormField<unknown>
    if (validationField.validation) {
      return validateFormField(validationField)
    }
    return field
  })
}

export function fieldsIsValid(fields: Array<FormFieldTypes>): boolean {
  return (
    fields.filter((field) => (field as ValidationFormField<unknown>)?.variant === 'error' || false).length === 0
  )
}

export function formIsValid<T extends StringMap>(localState: T): boolean {
  return (
    Object.keys(localState).filter(
      (key: string) => (localState[key] as ValidationFormField<unknown>)?.variant === 'error'
    ).length === 0
  )
}

export function toObject<T extends FormField<unknown>>(
  fields: Array<T>,
  includeSenstive = true
): Record<string, unknown> {
  return Object.assign(
    {},
    ...fields
      .filter((field) => !field.readonly)
      .filter((field) => !field.isSensitive || includeSenstive)
      .flatMap((field) => [{ [field.fieldName]: valueOrNull(field.value) }])
  )
}

export function fieldsToObject(fields: FormMap, options?: { includeSenstive: boolean }): Record<string, unknown> {
  return toObject(Object.values(fields), options?.includeSenstive)
}

export const nullValue = 'null'
export function valueOrNull(val: unknown) {
  return val === undefined || val === '' || val === nullValue ? null : val
}

export const parseIntOrUndefined = (value: string | undefined): number | undefined => {
  if (value === undefined) return undefined
  const parsedValue = parseInt(value, 10)
  return isNaN(parsedValue) ? undefined : parsedValue
}
